import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import i18n from '../i18n';

const statsCallable = httpsCallable(functions, 'getProductionStats');

// Per-period cache: { data, fetchedAt }
const cache = {};
const inflight = {};
const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

function isCacheFresh(period) {
  const cached = cache[period];
  return cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS;
}

async function fetchStats(period) {
  if (isCacheFresh(period)) return cache[period].data;
  if (inflight[period]) return inflight[period];

  inflight[period] = statsCallable({ period })
    .then((result) => {
      cache[period] = { data: result.data, fetchedAt: Date.now() };
      return result.data;
    })
    .finally(() => {
      delete inflight[period];
    });

  return inflight[period];
}

/** Warm the cache before navigating to Insights. Safe to call repeatedly. */
export function prefetchProductionStats(period = 'today') {
  if (isCacheFresh(period)) return Promise.resolve(cache[period].data);
  return fetchStats(period).catch((err) => {
    console.warn('[Floora] prefetchProductionStats failed:', err);
    return null;
  });
}

/** Prefetch other periods when the browser is idle. */
export function prefetchAllProductionStats() {
  prefetchProductionStats('today');
  const schedule = typeof requestIdleCallback === 'function'
    ? requestIdleCallback
    : (cb) => setTimeout(cb, 400);
  schedule(() => {
    prefetchProductionStats('thisWeek');
    prefetchProductionStats('thisMonth');
  });
}

export function useProductionStats(period = 'today') {
  const [data, setData] = useState(() => cache[period]?.data ?? null);
  const [loading, setLoading] = useState(!isCacheFresh(period));
  const [error, setError] = useState(null);
  const periodRef = useRef(period);
  periodRef.current = period;

  const load = useCallback(async (force = false) => {
    if (!force && isCacheFresh(period)) {
      setData(cache[period].data);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchStats(period);
      if (periodRef.current === period) {
        setData(result);
      }
    } catch (err) {
      console.error('[Floora] getProductionStats failed:', err);
      if (periodRef.current === period) {
        setError(err.message || i18n.t('loadErrors.stats'));
      }
    } finally {
      if (periodRef.current === period) {
        setLoading(false);
      }
    }
  }, [period]);

  useEffect(() => {
    if (isCacheFresh(period)) {
      setData(cache[period].data);
      setLoading(false);
      setError(null);
    } else {
      load();
    }
  }, [period, load]);

  return { data, loading, error, reload: () => load(true) };
}
